import { useCallback, useEffect, useState } from "react";
import type { ParticipationPromptPublic, ParticipationToday } from "@tih/shared";
import { fetchParticipationPrompts, fetchParticipationToday } from "../api/client";
import { useReveal } from "../hooks/useReveal";
import { useSiteCopy } from "../context/SiteCopyContext";
import { CopyText } from "../components/CopyText";
import { getAtPath } from "../lib/siteCopyPaths";
import { EventProximityBanner } from "../components/participation/EventProximityBanner";
import { ParticipationPromptGrid } from "../components/participation/ParticipationPromptGrid";
import { ParticipationWall } from "../components/participation/ParticipationWall";
import { EventPrepChecklist } from "../components/participation/EventPrepChecklist";

export function ParticipatePage() {
  const ref = useReveal<HTMLElement>();
  const { copy } = useSiteCopy();
  const [prompts, setPrompts] = useState<ParticipationPromptPublic[]>([]);
  const [today, setToday] = useState<ParticipationToday | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    Promise.all([fetchParticipationPrompts(), fetchParticipationToday()])
      .then(([p, t]) => {
        setPrompts(p.prompts);
        setToday(t);
      })
      .catch((e) => setError(String(e?.message ?? e)));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const showEventPrep =
    today?.featuredEvent &&
    today.daysOut != null &&
    today.daysOut <= 14 &&
    (today.proximity === "approach" ||
      today.proximity === "countdown" ||
      today.proximity === "eve" ||
      today.proximity === "announce");

  return (
    <section className="section participate-page" ref={ref}>
      <div className="sec-eyebrow">
        <em>
          <CopyText path="participate.eyebrow" as="span" />
        </em>
      </div>
      <h1 className="sec-title">
        <CopyText path="participate.title" as="span" />
      </h1>
      <p className="sec-lede" style={{ maxWidth: 720, marginBottom: 48 }}>
        <CopyText path="participate.lede" as="span" />
      </p>

      {error && <div className="form-error">{error}</div>}

      {today && (
        <EventProximityBanner
          proximity={today.proximity}
          featured={today.featuredEvent}
          daysOut={today.daysOut}
        />
      )}

      {showEventPrep && today?.featuredEvent && (
        <>
          <h2 className="sec-title sec-title-sm" style={{ marginBottom: 24 }}>
            {String(getAtPath(copy, "participate.eventPrepTitle") ?? "Getting ready for the overnight")}
          </h2>
          <ParticipationPromptGrid
            prompts={prompts}
            today={today}
            onUpdate={load}
            filterFamily="EVENT_PREP"
          />
          <div style={{ margin: "40px 0" }}>
            <EventPrepChecklist
              eventId={today.featuredEvent.id}
              items={today.prepChecklist}
            />
          </div>
        </>
      )}

      <h2 className="sec-title sec-title-sm" style={{ marginBottom: 24 }}>
        {String(getAtPath(copy, "participate.ministryTitle") ?? "Ministry prompts")}
      </h2>
      <ParticipationPromptGrid
        prompts={prompts}
        today={today}
        onUpdate={load}
        filterFamily="MINISTRY"
      />

      {today && (
        <div style={{ marginTop: 56 }}>
          <ParticipationWall items={today.wall} spotlightSlug={today.spotlightSlug} />
        </div>
      )}
    </section>
  );
}
